import moment from 'moment'
import queryString from 'query-string'
import { isArray, isEmpty, cloneDeep } from 'lodash'
import pluralize from 'pluralize'

import { fetchingAPI, apiService } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchAudioForCallId, fetchVideoForCallId } from '@/reducers/media/media.actions'
import { getTagsWithCategories } from '@/utils/helpers'

import { RECENT_QUESTIONS_COACHING_FEATURE_NAME } from '@/utils/constants'
import { fetchSavedFilters } from '../savedFilters/savedFilters.actions'
import * as redux from './tasks.redux'
import {
  INBOX,
  IGNORED,
  INBOX_PAGE_LIMIT,
  INBOX_DEFAULT_DATE_RANGE_DAYS,
  READY_FOR_REVIEW,
} from './tasks.constants'
import {
  CALL_DETAIL_PAGE,
  COACHING_CALL_DETAIL_PAGE,
  fetchCall,
} from '../callSearch/callSearch.actions'
import { getPatchedTaskText } from './tasks.helpers'
import { fetchRecentQuestions } from '../recentQuestions/recentQustions.actions'

export function fetchTasks(taskTypes = [], agentIds = [], tagIds = []) {
  return async (dispatch) => {
    try {
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - INBOX_DEFAULT_DATE_RANGE_DAYS)
      startDate.setHours(0, 0, 0, 0)

      const endDate = new Date()
      endDate.setHours(23, 59, 59, 999)
      // if agent_ids/tag_ids becomes too long, we may need to change endpoint to a post
      const params = queryString.stringify({
        task_types: taskTypes,
        agent_ids: agentIds,
        tag_ids: tagIds,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        task_statuses: ['completed', 'expired', 'ignored', 'ready_for_review'],
      })

      dispatch(redux.setLoading({ [INBOX]: true }))
      const response = await fetchingAPI(`${apiService.workflow}/tasks?${params}`, 'GET', dispatch)

      dispatch(redux.setTasks(response))
    } catch (err) {
      toast.error('Failed to fetch tasks', { cause: err })
    } finally {
      dispatch(redux.setLoading({ [INBOX]: false }))
    }
  }
}

// Identity of the most recent list request. The reducer stores whatever response arrives,
// so without this the last response to LAND would win rather than the last one SENT: the
// previous inbox's list landing after the user navigated away, or page 1 landing after a
// click to page 2. A response from any earlier request is ignored, and it must not clear
// the loading flag either, because the newer request still owns it.
let latestListRequestId = 0

export const fetchTasksPaginated =
  ({
    taskTypes = [],
    agentIds = [],
    tagIds = [],
    page = 1,
    limit = INBOX_PAGE_LIMIT,
    sortBy = 'updated_at',
    sortOrder = 'desc',
    taskStatuses = null,
    agentReviewed = null,
    createdByUserId = null,
    aiGenerated = null,
    taskName = null,
  } = {}) =>
  async (dispatch) => {
    latestListRequestId += 1
    const requestId = latestListRequestId
    const isLatest = () => requestId === latestListRequestId

    dispatch(redux.setLoading({ [INBOX]: true }))

    try {
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - INBOX_DEFAULT_DATE_RANGE_DAYS)
      startDate.setHours(0, 0, 0, 0)

      const endDate = new Date()
      endDate.setHours(23, 59, 59, 999)

      const params = queryString.stringify(
        {
          task_types: taskTypes,
          agent_ids: agentIds,
          tag_ids: tagIds,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          page,
          limit,
          sort_by: sortBy,
          sort_order: sortOrder,
          task_statuses: taskStatuses || ['completed', 'expired', 'ignored', 'ready_for_review'],
          agent_reviewed: agentReviewed,
          created_by_user_id: createdByUserId,
          ai_generated: aiGenerated,
          task_name: taskName,
        },
        { skipNull: true }
      )

      const url = `${apiService.workflow}/tasks/paginated?${params}`
      const response = await fetchingAPI(url, 'GET', dispatch)

      if (isLatest()) {
        dispatch(redux.setPaginatedTasks(response))
      }

      return response
    } catch (error) {
      if (isLatest()) {
        toast.error('Failed to fetch tasks', { cause: error })
      }
      return { tasks: [], total_count: 0, page: 1, limit: INBOX_PAGE_LIMIT, total_pages: 0 }
    } finally {
      if (isLatest()) {
        dispatch(redux.setLoading({ [INBOX]: false }))
      }
    }
  }

export const getCriteriaScoreByIds = (criteriaScoreId) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores/criteria_scores/by_ids?criteria_score_ids=${criteriaScoreId}`,
      'GET',
      dispatch
    )
    return response
  } catch (err) {
    toast.error('Failed to get criteria scores', { cause: err })
    return []
  }
}

export const createManualTask =
  (callId, userId, organizationId, name, reason, timestamp, type) => async (dispatch) => {
    try {
      const body = {
        name,
        call_id: callId,
        agent_id: userId,
        description: reason,
        type,
        status: 'ready_for_review',
        identifier_id: String(callId),
      }
      if (timestamp) {
        body.manual_timestamp = timestamp
      }
      await fetchingAPI(
        `${apiService.workflow}/tasks?requested_organization_id=${organizationId}`,
        'POST',
        dispatch,
        JSON.stringify(body)
      )
      const successMessage = type === 'compliance' ? 'Call escalated' : 'Coaching task created'
      toast.success(successMessage)
    } catch (err) {
      const failureMessage =
        type === 'compliance' ? 'Failed to escalate call' : 'Failed to create coaching task'
      toast.error(failureMessage, { cause: err })
    }
  }

// Fetch call detail + audio + screen capture for a SINGLE evidence call, on
// demand — i.e. when the user loads that call's recording. This used to run
// eagerly for every evidence call the moment a coaching task opened (a
// fetchMediaForEvidence loop over the whole list), firing GET /api/calls/{id} +
// /audio + /screen_capture per related call. A task with many calls therefore
// burst into hundreds of concurrent requests and could trip per-endpoint rate
// limits. The desktop coaching flow already loads on demand (RTG3-790); this
// brings the web coaching inbox in line.
export const fetchMediaForEvidenceCall = async (evidence, dispatch) => {
  const callId = evidence.call_id

  // Start all async fetches in parallel
  const [callData, audioResponse, videoResponse] = await Promise.all([
    dispatch(fetchCall(callId)),
    fetchAudioForCallId(callId, dispatch),
    fetchVideoForCallId(callId, dispatch),
  ])

  return {
    ...evidence,
    call_data: callData,
    call_audio: audioResponse
      ? {
          call_id: callId,
          audio_url: audioResponse.audioUrl,
          audio_status: audioResponse.audioStatus,
          audio_url_expiration: audioResponse.audioUrlExpiration,
        }
      : {
          call_id: callId,
          audio_status: 'unavailable',
        },
    call_video: videoResponse
      ? {
          call_id: callId,
          video_urls: videoResponse.videoUrls,
          video_status: videoResponse.videoStatus,
        }
      : {
          call_id: callId,
          video_status: 'unavailable',
        },
  }
}

export const fetchScorecardByIdentifierId = (originalTask) => async (dispatch) => {
  try {
    const [response] = await fetchingAPI(
      `${apiService.scorecard}/scoring/scores?scorecard_ids=${originalTask.scorecard_id}&requested_organization_id=${originalTask.organization_id}&include_deleted=True`,
      'GET',
      dispatch
    )

    const task = cloneDeep(originalTask)
    task.score = response

    const manualCriteria = []

    task.score.section_scores.forEach((section) => {
      section.measure_scores.forEach((measure) => {
        measure.criteria_scores.forEach((criteria) => {
          if (criteria.criteria_type === 'manual') {
            manualCriteria.push(criteria)
          }
        })
      })
    })
    task.manualCriteria = manualCriteria
    dispatch(redux.setTask(task))
  } catch (err) {
    toast.error('Failed to fetch scorecard', { cause: err })
  }
}

export const resolveManualScore =
  (updatedCriteria, scorecardId, organizationId) => async (dispatch) => {
    const params = queryString.stringify(
      { requested_organization_id: organizationId },
      { skipNull: true }
    )
    const url = `${apiService.scorecard}/scoring/scores/${scorecardId}/update_criteria?${params}`

    try {
      await fetchingAPI(url, 'POST', dispatch, JSON.stringify(updatedCriteria))
    } catch {
      toast.error('Failed to update scores')
    }
  }

export const fetchTaskById =
  (taskId, handleOpenTaskDrawer, handleCloseTaskDrawer, organizationId, showHiddenEvidence) =>
  async (dispatch, getState) => {
    dispatch(redux.setLoading({ task: true }))

    try {
      const params = queryString.stringify(
        {
          requested_organization_id: organizationId || undefined,
          // Dismissed reported calls are hidden rows; the prompt-feedback drawer needs them
          // back to render its Dismissed view.
          show_hidden_evidence: showHiddenEvidence ? 'true' : undefined,
        },
        { skipNull: true }
      )
      const url = params
        ? `${apiService.workflow}/tasks/${taskId}?${params}`
        : `${apiService.workflow}/tasks/${taskId}`
      const response = await fetchingAPI(url, 'GET', dispatch)

      const { tasks } = getState().tasks
      const found = tasks.some((task) => task.id === response.id)
      const newTasks = found
        ? tasks.map((task) => (task.id === response.id ? response : task))
        : [response, ...tasks]
      dispatch(redux.setTasks(newTasks))
      dispatch(redux.setTask(response))

      if (handleOpenTaskDrawer) {
        handleOpenTaskDrawer(response)
      }
      return response
    } catch (err) {
      toast.error('Failed to load task', { cause: err })
      if (handleCloseTaskDrawer) {
        handleCloseTaskDrawer()
      }
      return null
    } finally {
      dispatch(redux.setLoading({ task: false }))
    }
  }

export const patchTask = (taskId, body = {}, inboxType, organizationId) => {
  return async (dispatch) => {
    try {
      const url = organizationId
        ? `${apiService.workflow}/tasks/${taskId}?requested_organization_id=${organizationId}`
        : `${apiService.workflow}/tasks/${taskId}`
      const response = await fetchingAPI(url, 'PATCH', dispatch, JSON.stringify(body))
      await dispatch(fetchTaskById(response.id, null, null, organizationId))
      const text = getPatchedTaskText(body, inboxType)
      if (body.status !== IGNORED) {
        toast.success(text)
      }
    } catch (err) {
      toast.error('Failed to update', { cause: err })
    }
  }
}

export const renameTask = (taskId, name, organizationId) => async (dispatch) => {
  try {
    const params = organizationId ? `?requested_organization_id=${organizationId}` : ''
    const url = `${apiService.workflow}/tasks/${taskId}${params}`
    const response = await fetchingAPI(url, 'PATCH', dispatch, JSON.stringify({ name }))

    // Refreshes both the drawer's task and its row in the inbox list.
    await dispatch(fetchTaskById(response.id, null, null, organizationId))
    toast.success('Title updated')

    return response
  } catch (err) {
    toast.error('Failed to update title', { cause: err })
    return null
  }
}

export const bulkPatchTasks = (taskIds, updates, organizationId, filters) => async (dispatch) => {
  try {
    const params = organizationId ? `?requested_organization_id=${organizationId}` : ''
    const url = `${apiService.workflow}/tasks/bulk${params}`
    const body = filters ? { filters, updates } : { task_ids: taskIds, updates }

    const result = await fetchingAPI(url, 'PATCH', dispatch, JSON.stringify(body))

    const updatedCount = result?.updated?.length || 0
    const action = updates.status === READY_FOR_REVIEW ? 'reopened' : 'dismissed'
    toast.success(`${pluralize('task', updatedCount, true)} ${action}`)

    return result
  } catch (error) {
    toast.error('Failed to update tasks', { cause: error })
    return null
  }
}

export const fetchInitialFilters = (filterType) => async (dispatch, getState) => {
  await dispatch(fetchSavedFilters(filterType))
  const filters = getState().savedFilters.savedFilterList[filterType]

  // Find the default saved filter, if exists
  const defaultFilter = filters.find((filter) => filter.is_default)

  if (defaultFilter) {
    dispatch(redux.setSavedFilterId(defaultFilter.uuid))
  }

  dispatch(redux.setFiltersLoaded(true))
}

export const fetchOrganizationData = (organizationId) => async (dispatch) => {
  dispatch(redux.setLoading({ agents: true, tags: true }))

  try {
    const [userResponse, tags, tagCategories] = await Promise.all([
      fetchingAPI(`${apiService.core}/api/organizations/${organizationId}/users`, 'GET', dispatch),
      fetchingAPI(`${apiService.core}/api/organizations/${organizationId}/tags`, 'GET', dispatch),
      fetchingAPI(
        `${apiService.core}/api/organizations/${organizationId}/tags/categories`,
        'GET',
        dispatch
      ),
    ])

    const tagsOptionsWithCategories = getTagsWithCategories(tags, tagCategories)

    userResponse.users.sort((a, b) => a.first_name.localeCompare(b.first_name))
    const agentOptions = userResponse.users.map((agent) => ({
      value: agent.id,
      label: `${agent.first_name} ${agent.last_name}`,
    }))

    dispatch(redux.setData({ agents: agentOptions, tags: tagsOptionsWithCategories }))
  } catch (err) {
    toast.error('Failed to fetch organization data', { cause: err })
  } finally {
    dispatch(redux.setLoading({ agents: false, tags: false }))
  }
}

export const submitCoachingRequest =
  (payload, fetchTasksWithFilters, handleOpenTaskDrawer, handleResetInterval, signal, signalDone) =>
  async (dispatch, getState) => {
    dispatch(redux.setLoading({ coachingRequest: true }))

    try {
      const response = await fetchingAPI(
        `${apiService.summary}/coaching`,
        'POST',
        dispatch,
        JSON.stringify(payload),
        undefined,
        undefined,
        undefined,
        signal
      )

      if (response) {
        handleOpenTaskDrawer(response)
        // Add the new coaching task to the tasks list in redux
        const { tasks } = getState().tasks
        dispatch(redux.setTasks([response, ...tasks]))
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        toast.error('Coaching request was cancelled.', { cause: err })
      } else {
        toast.error(`Failed to generate coaching task. ${err?.message || 'Please try again.'}`, {
          cause: err,
        })
      }
    } finally {
      if (signalDone) {
        signalDone()
      }
      handleResetInterval()
      dispatch(redux.setLoading({ coachingRequest: false }))
      dispatch(fetchRecentQuestions(RECENT_QUESTIONS_COACHING_FEATURE_NAME))
    }
  }

export const fetchMoreEvidenceForTask = (taskId, unhideEvidenceType) => async (dispatch) => {
  dispatch(redux.setLoading({ task: true }))

  try {
    const updatedTask = await fetchingAPI(
      `${apiService.workflow}/tasks/${taskId}?unhide_evidence_count=5&unhide_evidence_type=${unhideEvidenceType}`,
      'GET',
      dispatch
    )
    dispatch(redux.setTask(updatedTask))
  } catch (err) {
    toast.error(err?.message || 'Failed to fetch more evidence for task')
  } finally {
    dispatch(redux.setLoading({ task: false }))
  }
}

export const deleteEvidenceFromTask = (taskId, callId) => async (dispatch, getState) => {
  const { callIdDeleteQueue } = getState().tasks

  if (callIdDeleteQueue.includes(callId)) {
    try {
      await fetchingAPI(
        `${apiService.workflow}/tasks/${taskId}/evidence/by_call_id/${callId}`,
        'DELETE',
        dispatch
      )
      toast.success('Call evidence removed')
      dispatch(redux.setCallIdDeleteCompleted(callId))
      await dispatch(fetchTaskById(taskId))
    } catch (err) {
      toast.error('Failed to remove call evidence', { cause: err })
      dispatch(redux.removeFromCallIdDeleteQueue(callId))
    }
  }
}

export const deleteEvidenceInQueueAndReset =
  (taskId, fetchTasksWithFilters) => async (dispatch, getState) => {
    const { callIdDeleteQueue, callIdDeleteCompleted, callCountIncreased } = getState().tasks

    if (!isEmpty(callIdDeleteQueue)) {
      console.info(
        '[DELETE QUEUE]: Attempting to delete remaining call evidence in queue:',
        callIdDeleteQueue
      )

      const promises = callIdDeleteQueue.map((callId) =>
        fetchingAPI(
          `${apiService.workflow}/tasks/${taskId}/evidence/by_call_id/${callId}`,
          'DELETE',
          dispatch
        )
      )

      try {
        await Promise.all(promises)
        toast.success('Call evidence removed')
      } catch (err) {
        toast.error('Failed to delete call evidence', { cause: err })
      } finally {
        console.info('[TASKS]: Closing drawer and refetching tasks to keep in sync with database')
        dispatch(redux.resetCallQueues())
        fetchTasksWithFilters()
      }
    } else {
      const shouldRefetchData = !isEmpty(callIdDeleteCompleted) || callCountIncreased
      console.info('[DELETE QUEUE]: No call evidence in delete queue, resetting')
      dispatch(redux.resetCallQueues())

      if (shouldRefetchData) {
        console.info('[TASKS]: Closing drawer and refetching tasks to keep in sync with database')
        fetchTasksWithFilters()
      }
    }
  }

export const fetchManagerAudit = (taskTypes = ['coaching']) => {
  return async (dispatch, getState) => {
    dispatch(redux.setLoading({ managerAudit: true }))

    try {
      const { filters } = getState().tasks

      const managerAuditParams = queryString.stringify(
        {
          start_date: filters.startDate,
          end_date: filters.endDate,
          task_types: taskTypes,
          requested_organization_id: filters.organizationId,
        },
        { skipNull: true }
      )

      const pageViewsParams = queryString.stringify(
        {
          start_date: filters.startDate,
          end_date: filters.endDate,
          pages: [CALL_DETAIL_PAGE, COACHING_CALL_DETAIL_PAGE],
          requested_organization_id: filters.organizationId,
        },
        { skipNull: true }
      )

      const [managerAuditResponse, pageViewsResponse] = await Promise.all([
        fetchingAPI(
          `${apiService.workflow}/tasks/manager_audit?${managerAuditParams}`,
          'GET',
          dispatch
        ),
        fetchingAPI(
          `${apiService.reporting}/page-views/counts?${pageViewsParams}`,
          'GET',
          dispatch
        ),
      ])

      const pageViewsByUserId = {}
      pageViewsResponse.forEach((userPageViews) => {
        pageViewsByUserId[userPageViews.user_id] = userPageViews.views_by_page
      })

      const mergedResponse = managerAuditResponse.map((manager) => {
        const viewsByPage = pageViewsByUserId[manager.manager_id] || {}
        return {
          ...manager,
          statuses: {
            ...manager.statuses,
            calls_viewed: viewsByPage[CALL_DETAIL_PAGE] || 0,
            coaching_calls_viewed: viewsByPage[COACHING_CALL_DETAIL_PAGE] || 0,
          },
        }
      })

      dispatch(redux.setData({ managerAudit: mergedResponse }))
    } catch (err) {
      toast.error('Failed to fetch supervisor audit data', { cause: err })
    } finally {
      dispatch(redux.setLoading({ managerAudit: false }))
    }
  }
}

export const fetchActiveCoachingForAgent = async (dispatch, agentId) => {
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/coaching/agent_active_coaching_config/${agentId}`,
      'GET',
      dispatch
    )
    if (isArray(response)) {
      return response[0]
    }
    return null
  } catch (err) {
    toast.error('Failed to fetch active coaching configuration for this agent', { cause: err })
    return null
  }
}

export const updateActiveCoachingConfig = async (dispatch, configId, body) => {
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/coaching/agent_active_coaching_config/${configId}`,
      'PUT',
      dispatch,
      JSON.stringify(body)
    )
    toast.success('AI Coaching configuration updated for this agent')
    return response
  } catch (err) {
    toast.error('Failed to update AI coaching configuration for this agent', { cause: err })
    return null
  }
}

export const createActiveCoachingForAgent = async (dispatch, agentId, taskId, question) => {
  const body = {
    task_id: taskId,
    is_active: true,
    expiration_date: moment().add(14, 'days').toISOString(),
    coaching_question: question,
  }
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/coaching/agent_active_coaching_config/${agentId}`,
      'POST',
      dispatch,
      JSON.stringify(body)
    )
    toast.success('AI Coaching is now active for this agent')
    await dispatch(fetchTaskById(taskId))
    return response
  } catch (err) {
    toast.error('Failed to set up AI coaching for this agent', { cause: err })
    return null
  }
}
